import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PremioAPI from '../premio-api';
import Alert from '../../../components/common/Alert';
import Button from '../../../components/common/Button';

const stateLabel = { waiting: 'En espera', open: 'Abierta', closed: 'Cerrada' };
const fieldStyle = { display: 'block', width: '100%', minHeight: 48, padding: 10, fontSize: 16, marginTop: 6 };
const panelStyle = { padding: 20, border: '1px solid #ddd', borderRadius: 8, marginBottom: 20 };

function errorDetail(error) {
  const detail = error && error.data && error.data.detail;
  return typeof detail === 'object' ? detail : { detail: detail || 'No fue posible actualizar la votación.' };
}

function localDate(value) {
  if (!value) return '';
  const date = new Date(value);
  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function EmergeVotingControl({ convocatoriaId, isDemo = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('waiting');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingClose, setPendingClose] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const response = await PremioAPI.fetchEmergeVotingState(convocatoriaId);
      setSnapshot(response.data);
      setError('');
    } catch (cause) { setError(errorDetail(cause).detail); }
  }, [convocatoriaId]);

  useEffect(() => {
    setSnapshot(null);
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [refresh]);

  const categories = snapshot ? snapshot.categories.slice().sort((a, b) => a.order - b.order) : [];
  const category = categories.find(item => item.id === selectedId) || categories[0];
  const control = category && snapshot.controls[category.id];
  const info = category && snapshot.admin.categories[category.id];
  const selectedCategoryId = category && category.id;
  const savedStatus = control && control.status;
  const savedDeadline = control && control.deadlineAt;

  useEffect(() => {
    setStatus(savedStatus || 'waiting');
    setDeadline(localDate(savedDeadline));
    setPendingClose(null);
    setMessage('');
  }, [selectedCategoryId, savedStatus, savedDeadline]);

  async function apply(force) {
    if (!category || busy) return;
    if (deadline && !Number.isFinite(new Date(deadline).getTime())) {
      setError('Ingresa una fecha y hora válidas.'); return;
    }
    setBusy(true); setError(''); setMessage('');
    try {
      await PremioAPI.updateEmergeVotingControl(convocatoriaId, category.id, {
        status, deadlineAt: deadline ? new Date(deadline).toISOString() : null, force: Boolean(force),
      });
      setPendingClose(null);
      await refresh(); setMessage('Categoría actualizada.');
    } catch (cause) {
      const detail = errorDetail(cause);
      if (detail.code === 'PENDING_VOTES') setPendingClose(detail.pendingCount);
      else setError(detail.detail);
    } finally { setBusy(false); }
  }

  return <div style={{ maxWidth: 600, margin: '0 auto' }}>
    <h2>{isDemo ? 'Premio de prueba · Votación por categorías' : 'Votación Emerge por categorías'}</h2>
    <p>{isDemo ? 'Puedes practicar con las cinco categorías y revisar los resultados de prueba.' : 'Abre cada categoría durante la jornada. Todas comienzan en espera. Los horarios del programa son orientativos.'}</p>
    {error && <Alert type="error">{error}</Alert>}
    {message && <p role="status">{message}</p>}
    {!snapshot && !error && <p role="status">Cargando votación…</p>}
    {category && <>
      <label>Categoría
        <select style={fieldStyle} value={category.id} onChange={event => { setSelectedId(event.target.value); setMessage(''); setPendingClose(null); }}>
          {categories.map(item => <option key={item.id} value={item.id}>{item.name} · {stateLabel[snapshot.controls[item.id].status]}</option>)}
        </select>
      </label>
      <section style={{ ...panelStyle, marginTop: 20 }}>
        <h3>Apertura y cierre</h3>
        <label>Estado<select style={fieldStyle} value={status} disabled={busy} onChange={event => { setStatus(event.target.value); setPendingClose(null); }}>
          <option value="waiting">En espera</option><option value="open">Abierta</option><option value="closed">Cerrada</option>
        </select></label>
        <label style={{ display: 'block', marginTop: 16 }}>Cierre automático opcional · hora de este dispositivo
          <input style={fieldStyle} type="datetime-local" value={deadline} disabled={busy} onChange={event => { setDeadline(event.target.value); setPendingClose(null); }} />
        </label>
        <p>Sin fecha, el cierre es manual. Cerrar o reabrir no borra votos ni permite editar confirmaciones existentes.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Button secondary disabled={busy || !deadline} onClick={() => { setDeadline(''); setPendingClose(null); }}>Quitar plazo</Button>
          <Button loading={busy} onClick={() => apply(false)}>Aplicar cambios</Button>
        </div>
        {pendingClose !== null && <div role="alert" style={{ paddingTop: 16 }}>
          <p>Quedan {pendingClose} jurados sin confirmar. Si cierras, no podrán seguir votando esta categoría.</p>
          <Button secondary disabled={busy} onClick={() => setPendingClose(null)}>Cancelar cierre</Button>{' '}
          <Button disabled={busy} onClick={() => apply(true)}>Cerrar de todas formas</Button>
        </div>}
      </section>
      <section style={panelStyle}>
        <h3>Avance de los jurados</h3>
        <p><strong>{info.confirmedCount} de {info.eligibleCount}</strong> votaciones confirmadas · {info.eligibleCount - info.confirmedCount} pendientes</p>
        <ul style={{ listStyle: 'none', padding: 0 }}>{info.voters.map(voter => <li key={voter.jurorId} style={{ padding: '12px 0', borderTop: '1px solid #eee' }}>
          <strong>{voter.name}</strong><br />
          <span style={{ overflowWrap: 'anywhere' }}>{voter.email}</span><br />
          <span>{voter.confirmedAt ? 'Votación confirmada' : `${voter.completedFinalists} de 4 proyectos evaluados`}</span>
        </li>)}</ul>
      </section>
      <section style={panelStyle}>
        <h3>Resultados confirmados</h3>
        <p>Promedio ponderado, escala 1 a 5. Se consideran solo votaciones confirmadas; los empates comparten posición.</p>
        <ol style={{ listStyle: 'none', padding: 0 }}>{info.results.map(row => <li key={row.finalistId} style={{ padding: '12px 0', borderTop: '1px solid #eee' }}>
          <strong>{row.position ? `${row.position}. ` : ''}{row.name}</strong><br />
          <span>{row.averageScore == null ? 'Sin votos confirmados' : `${Number(row.averageScore).toLocaleString('es-CL', { maximumFractionDigits: 3 })} puntos · ${row.voteCount} votos`}</span>
        </li>)}</ol>
      </section>
      <p style={{ fontSize: 13 }}>Se actualiza cada 5 segundos. Los borradores no se incluyen en los resultados.</p>
    </>}
  </div>;
}

EmergeVotingControl.propTypes = { convocatoriaId: PropTypes.string.isRequired, isDemo: PropTypes.bool };
